import MapboxDraw, { DrawCustomModeThis, DrawFeature, DrawLineString, DrawPoint } from "@mapbox/mapbox-gl-draw";
import booleanEqual from "@turf/boolean-equal";
import booleanPointOnLine from "@turf/boolean-point-on-line";
import { Feature, LineString, point } from "@turf/helpers";
import { Feature as GeoJSONFeature, GeoJsonProperties, Geometry } from "geojson";
import { isEqual } from "lodash";
import mapboxgl from "mapbox-gl";

import { DRAWING_CONSTANTS, DRAWING_PROPERTIES_TO_EXCLUDE } from "fond/constants";
import { captureBeforeDeleteFeatures, validateAfterDelete, ValidationState } from "fond/draw/validation/helper";
import { safeNearestPointOnLine } from "fond/turf";

const { constants: Constants } = MapboxDraw;

/**
 * Configuration specifying which layer IDs to use for topology operations.
 * Allows the same topology logic to be reused across aerial (span/pole) and underground (path/point) layers.
 */
export interface TopologyConfig extends NodeTopologyConfig {
  lineLayerId: string;
}

/**
 * A topology whose line layer may be absent (e.g. poles without spans).
 */
export interface NodeTopologyConfig {
  lineLayerId?: string;
  pointLayerId: string;
}

/**
 * Layer IDs of a network's underground and aerial topologies.
 */
export interface NetworkTopologyConfig {
  underground: TopologyConfig;
  aerial: NodeTopologyConfig;
}

/**
 * Retrieves the ID of a feature.
 * Checks both the feature's ID property and its properties.id, since
 * Mapbox Draw does not always set the ID property (e.g., when a feature is created).
 */
export const getFeatureId = (feature: DrawFeature | Feature<Geometry, GeoJsonProperties>): string | number | undefined =>
  feature.id || feature.properties?.id;

/**
 * Checks if a line feature is valid by ensuring that not all coordinates are identical.
 * A line with all vertices at the same location is degenerate and should be removed.
 */
export const validLine = (feature: DrawLineString): boolean => {
  const coordinates = feature.getCoordinates();
  return !coordinates.every((coords) => isEqual(coordinates[0], coords));
};

/**
 * Splits a line into two segments at the given point location.
 * Used when a point is dropped on a line to split it at that location.
 * The original line is deleted and replaced with two new lines.
 *
 * @param config - Layer IDs for the line and point layers
 * @param path - The line to be split
 * @param splitPoint - The point location where the line will be split
 * @returns An array of new line features created from the split
 */
export function splitPaths(this: DrawCustomModeThis, config: TopologyConfig, path: DrawLineString, splitPoint: DrawPoint): DrawLineString[] {
  const newFeatures: DrawLineString[] = [];
  const splitCoordinates = splitPoint.getCoordinates();
  const pathCoords = path.getCoordinates();

  // Check if the point lands on an existing vertex.
  const existingVertexIndex = pathCoords.findIndex((c) => booleanEqual(point(c), point(splitCoordinates), { precision: 6 }));

  let segments: number[][][];

  if (existingVertexIndex > 0 && existingVertexIndex < pathCoords.length - 1) {
    // Point is on an interior vertex — split at that vertex directly.
    segments = [pathCoords.slice(0, existingVertexIndex + 1), pathCoords.slice(existingVertexIndex)];
  } else if (existingVertexIndex === -1) {
    // Point is between vertices — insert it and split.
    const nearestPoint = safeNearestPointOnLine(path, splitPoint);
    if (!nearestPoint?.properties) return newFeatures;

    const newCoords = [...pathCoords];
    const splitIndex = nearestPoint.properties.index + 1;
    newCoords.splice(splitIndex, 0, splitCoordinates);

    segments = [newCoords.slice(0, splitIndex + 1), newCoords.slice(splitIndex)];
  } else {
    // Point is on the first or last vertex — nothing to split.
    return newFeatures;
  }

  const domainProperties = Object.fromEntries(Object.entries(path.properties ?? {}).filter(([key]) => !DRAWING_PROPERTIES_TO_EXCLUDE.includes(key)));

  segments.forEach((coordinates) => {
    const newF = this.newFeature({
      type: Constants.geojsonTypes.FEATURE,
      properties: { ...domainProperties, layerId: config.lineLayerId },
      geometry: {
        type: Constants.geojsonTypes.LINE_STRING,
        coordinates,
      },
    }) as DrawLineString;

    newFeatures.push(newF);
    this.addFeature(newF);
  });

  // Delete the original path synchronously so the correct state is captured
  // by handleOnCreate's updateHistory(). Previously this was deferred with a
  // draw.validation event to amend history, but the defer fires after the mode
  // transition completes — by then the new drawing mode's onSetup has created
  // a placeholder LineString with degenerate coordinates ([[]]). Firing
  // draw.validation at that point causes updateHistorySilent to capture the
  // placeholder in history, crashing createSnapList on undo/redo.
  const featureId = getFeatureId(path);
  if (featureId) {
    this.deleteFeature(featureId, { silent: true });
  }

  return newFeatures;
}

/**
 * Checks for collocated features (lines or points) at the given feature's location.
 * Uses the Mapbox GL JS library to project the feature's coordinates to screen coordinates,
 * and then queries the map for features within a bounding box around the projected coordinates.
 *
 * @param config - Layer IDs for the line and point layers
 * @param feature - The feature to check for collocated features
 * @param connectedIds - Set of connected IDs to exclude from the search
 * @param type - The type of event (e.g. "mouseup") used for filtering
 * @returns An object containing arrays of collocated lines and points
 */
export function pullInFeatures(
  this: DrawCustomModeThis,
  config: NodeTopologyConfig,
  feature: DrawFeature,
  connectedIds?: Set<string | number>,
  type?: MapboxDraw.MapMouseEvent["type"]
): { lines: DrawLineString[]; points: DrawPoint[] } {
  const { x, y } = this._ctx.map.project(feature.getCoordinates() as mapboxgl.LngLatLike);
  const queryBbox: [mapboxgl.PointLike, mapboxgl.PointLike] = [
    [x - 15, y - 15],
    [x + 15, y + 15],
  ];
  const allFeatures = this.featuresAt(null, queryBbox, "click") as Feature[];
  const collocatedLines: DrawLineString[] = [];
  const collocatedPoints: DrawPoint[] = [];

  if (feature.type === "Point") {
    allFeatures
      // On mouseup we exclude the already connected lines
      .filter((existingFeature) => !(type === "mouseup" && connectedIds?.has(existingFeature.properties?.id)))
      .forEach((existingFeature) => {
        const existingFeatureId = getFeatureId(existingFeature);

        if (existingFeatureId) {
          // Get the related DrawFeature as we need to compare coordinates
          // found only on DrawFeatures, not Feature (which have different precisions)
          const drawFeature: DrawFeature | undefined = this.getFeature(existingFeatureId);

          if (drawFeature?.type === "LineString" && config.lineLayerId && drawFeature.properties?.layerId === config.lineLayerId) {
            // Check if the point is being dropped onto an existing vertex
            if (drawFeature.getCoordinates().some((c) => booleanEqual(point(c), point(feature.getCoordinates()), { precision: 5 }))) {
              collocatedLines.push(drawFeature);
            } else if (booleanPointOnLine(feature.getCoordinates(), drawFeature.toGeoJSON() as Feature<LineString>, { epsilon: 0.0000001 })) {
              // Check if the point is being dropped onto a line
              collocatedLines.push(drawFeature);
            }
          } else if (
            drawFeature?.type === "Point" &&
            existingFeatureId !== getFeatureId(feature) &&
            drawFeature.properties?.layerId === config.pointLayerId
          ) {
            // Check if a point is being dropped onto an existing point
            if (booleanEqual(drawFeature, feature, { precision: 6 })) {
              collocatedPoints.push(drawFeature);
            }
          }
        }
      });
  }

  return {
    lines: collocatedLines,
    points: collocatedPoints,
  };
}

/**
 * Retrieves the points at the endpoints of a line.
 * Queries for collocated point features at each coordinate of the line.
 *
 * @param config - Layer IDs for the line and point layers
 * @param feature - The line feature to check for endpoint points
 * @returns An array of DrawPoint objects found at the line's coordinates
 */
export function getLineEndpointPoints(this: DrawCustomModeThis, config: NodeTopologyConfig, feature: DrawLineString): DrawPoint[] {
  const points: DrawPoint[] = [];
  feature.getCoordinates().forEach((coordinates) => {
    const { points: p } = pullInFeatures.call(
      this,
      config,
      this.newFeature({
        type: Constants.geojsonTypes.FEATURE,
        properties: {},
        geometry: {
          type: Constants.geojsonTypes.POINT,
          coordinates,
        },
      })
    );

    points.push(...p);
  });

  return points;
}

/**
 * Checks if a point feature is snapped to any lines.
 * Sets the DRAWING_CONSTANTS.Unsnapped property based on whether connected lines exist,
 * allowing for visual styling based on connection status.
 *
 * @param config - Layer IDs for the line and point layers
 * @param feature - The point feature to check for snapped status
 */
export function refreshSnappedStatus(this: DrawCustomModeThis, config: NodeTopologyConfig, feature: DrawPoint): void {
  const { lines } = pullInFeatures.call(this, config, feature);
  feature.setProperty(DRAWING_CONSTANTS.Unsnapped, Boolean(lines.length === 0).toString());
}

/**
 * Deletes features with validation support.
 * Captures colocated features for re-validation before deletion,
 * performs the deletion, refreshes snapped status of affected points,
 * and validates colocated features after deletion.
 *
 * @param config - Layer IDs for the line and point layers
 * @param state - The mode state (must include ValidationState fields when validation is active)
 * @param deleteIds - IDs of features to delete
 * @param pointsToRefresh - Points whose snapped status should be refreshed after deletion
 */
export function deleteAndValidate(
  this: DrawCustomModeThis,
  config: NodeTopologyConfig,
  state: ValidationState,
  deleteIds: (string | number)[],
  pointsToRefresh: DrawPoint[]
): void {
  // Deduplicate IDs so capture and deletion operate on the same set
  const uniqueIds = [...new Set(deleteIds)];

  // Capture colocated features before deletion for re-validation
  const featuresToDelete = uniqueIds
    .map((id) => this.getFeature(id)?.toGeoJSON() as GeoJSONFeature | undefined)
    .filter((f): f is GeoJSONFeature => f != null);
  captureBeforeDeleteFeatures.call(this, state, featuresToDelete);

  this.deleteFeature(uniqueIds);

  pointsToRefresh.forEach((p) => {
    refreshSnappedStatus.call(this, config, p);
  });

  // Re-validate colocated features (e.g. points that lost line connections)
  validateAfterDelete.call(this, state);
}
